import DatePickerField from "components/forms/DatePickerField";
import { ErrorPositions } from "enums/errorPositions";
import LabelWithInfoTooltip from "components/labelWithInfoTooltip/labelWithInfoTooltip";
import { PeopleType } from "features/Library/PeopleAssignments/types";
import { type FormikProps } from "formik";
import moment from "moment";
import React from "react";
import { Radio } from "semantic-ui-react";
import dateTimeUtils from "utils/dateTimeUtils";
import cn from "classnames";

import { ValidatedFormWithoutFormik } from "../../forms";

import styles from "./flexibleDaysToCompleteInput.module.scss";

export interface FlexibleDaysToCompleteInputProps {
  peopleType: PeopleType;
  onValidChange?: (...args: any[]) => any;
  daysToComplete?: number;
  fixedDueDate?: string;
  isFixedDueDate?: boolean;
  onChange: Function;
  disabled?: boolean;
  nestedValuesPrefix?: string;
  errorPosition?: ErrorPositions;
  isReadOnlyView?: boolean;
}

function FlexibleDaysToCompleteInput(props: FlexibleDaysToCompleteInputProps & FormikProps<any>) {
  const {
    onValidChange,
    daysToComplete,
    errors,
    touched,
    isValid,
    handleBlur,
    setFieldTouched,
    setFieldValue,
    dirty,
    resetForm,
    fixedDueDate,
    isFixedDueDate,
    onChange,
    values,
    isSubmitting,
    isValidating,
    submitCount,
    peopleType,
    disabled,
    nestedValuesPrefix,
    isReadOnlyView,
    errorPosition = ErrorPositions.bottom,
  } = props;

  const validatedFieldProps = {
    errors,
    touched,
    isValid,
    handleBlur,
    setFieldTouched,
    setFieldValue,
    dirty,
    resetForm,
    values,
    isSubmitting,
    isValidating,
    submitCount,
  };

  const days = Number(daysToComplete) > 0 ? daysToComplete : "";
  const fixedDate = fixedDueDate ? dateTimeUtils.formatDate(fixedDueDate) : undefined;
  const minDate = moment().add(1, "days");
  const maxDate = moment().add(100, "days");

  const handleDateChange = (_: React.SyntheticEvent<HTMLElement>, data: { name: string; value: string }) => {
    let newDate = moment(new Date(`${data.value} ${moment().local().format("HH:mm:ss")}`));
    onChange("fixedDueDate")(newDate.toISOString() ?? data.value);
  };

  const daysToCompleteTooltipText =
    peopleType === PeopleType.User
      ? "Requires content to be completed within a set number of days from the date this user was added to this Flow."
      : "Requires content to be completed within a set number of days from the date they are added to the group, manually or automatically.";

  const fixedDueDateTooltipText =
    peopleType === PeopleType.User
      ? "Requires content to be completed by a fixed date, regardless of when users are added."
      : "Requires content to be completed by a fixed date, regardless of when users are added to the group.";

  const propertyNames = {
    daysToComplete: nestedValuesPrefix ? `${nestedValuesPrefix}.daysToComplete` : "daysToComplete",
    fixedDueDate: nestedValuesPrefix ? `${nestedValuesPrefix}.fixedDueDate` : "fixedDueDate",
  };

  if (isReadOnlyView) {
    return (
      <div className={styles.readOnlyWrapper}>
        <span>{isFixedDueDate ? "Fixed Date" : "Days to Complete"}</span>
        <div className={styles.readOnlyValue}>{isFixedDueDate ? fixedDate : days}</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <ValidatedFormWithoutFormik>
        <div className={styles.section}>
          <Radio
            name={`RadioGroup${values.id}`}
            value={`DaysToComplete${values.id}`}
            checked={!isFixedDueDate}
            onChange={() => {
              onChange("isFixedDueDate")(false);
            }}
            disabled={disabled}
          />
          <LabelWithInfoTooltip
            width={16}
            label="Days to complete"
            info={daysToCompleteTooltipText}
            labelClass={cn({ [styles.disabled]: isFixedDueDate || disabled })}
          />
          <ValidatedFormWithoutFormik.InputField
            className={styles.days}
            propertyName={propertyNames.daysToComplete}
            value={days}
            isFormValid={isValid}
            onIsFormValidChange={onValidChange}
            autoFocus
            maxLength={7}
            errorPosition={errorPosition}
            disabled={isFixedDueDate || disabled}
            handleChange={(_, data) => {
              onChange("daysToComplete")(parseInt(data!.value) || undefined);
            }}
            mask={/^([1-9]\d*|\b|^$)$/}
            {...validatedFieldProps}
          />
        </div>
        <div className={styles.section}>
          <Radio
            name={`RadioGroup${values.id}`}
            value={`FixedDate${values.id}`}
            checked={isFixedDueDate}
            onChange={() => {
              onChange("isFixedDueDate")(true);
            }}
            disabled={disabled}
          />
          <LabelWithInfoTooltip
            width={16}
            label="Fixed Date"
            info={fixedDueDateTooltipText}
            labelClass={cn({ [styles.disabled]: !isFixedDueDate || disabled })}
          />
          <div className={styles.date}>
            <DatePickerField
              {...props}
              selected={fixedDate}
              propertyName={propertyNames.fixedDueDate}
              minDate={dateTimeUtils.formatDate(minDate)}
              maxDate={dateTimeUtils.formatDate(maxDate)}
              disabled={!isFixedDueDate || disabled}
              onDateChange={handleDateChange}
              errorPosition={errorPosition}
              placeholder="Enter Date"
            />
          </div>
        </div>
      </ValidatedFormWithoutFormik>
    </div>
  );
}

export default FlexibleDaysToCompleteInput;
